import {ActionIcon, Button, MultiSelect, Select} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import {IconPlus, IconTrash} from '@tabler/icons-react';
import React, {useMemo} from 'react';
import type {
  WorkflowFormFieldPersonnelTarget} from 'src/api/ProactApiClient';
import {
  FormFieldTypeEnum,
  NotificationAttributeEnum
} from 'src/api/ProactApiClient';
import {SectionTitle} from 'src/design/atoms/Typography';
import {useGetFormDefinitionFieldListQuery} from 'src/redux/queries/formDefinition';

const PersonnelAttributeOptions = [
  {value: String(NotificationAttributeEnum.Assignee), label: 'Person'},
  {value: String(NotificationAttributeEnum.Supervisor), label: 'Supervisor'},
  {
    value: String(NotificationAttributeEnum.SuperSupervisor),
    label: "Supervisor's Supervisor",
  },
];

export interface FormFieldPersonnelTargetEditorProps {
  targets: WorkflowFormFieldPersonnelTarget[];
  formDefinitionId?: number;
  onTargetsChange: (targets: WorkflowFormFieldPersonnelTarget[]) => void;
}

export const FormFieldPersonnelTargetEditor: React.FC<
  FormFieldPersonnelTargetEditorProps
> = ({targets, formDefinitionId, onTargetsChange}) => {
  const {data: fieldsData} = useGetFormDefinitionFieldListQuery(
    {formDefinitionId: formDefinitionId!, start: 0, length: 10000},
    {skip: !formDefinitionId}
  );

  const personnelFieldOptions = useMemo(() => {
    if (!fieldsData?.data) return [];
    return fieldsData.data
      .filter(
        f =>
          f.formFieldType === FormFieldTypeEnum.DropdownSinglePersonnel &&
          !!f.invariantId
      )
      .map(f => ({value: f.invariantId!, label: f.label ?? f.invariantId!}));
  }, [fieldsData]);

  if (!formDefinitionId || personnelFieldOptions.length === 0) return null;

  const handleAdd = () => {
    onTargetsChange([
      ...targets,
      {
        formFieldInvariantId: personnelFieldOptions[0]?.value ?? '',
        attributes: [NotificationAttributeEnum.Assignee],
      },
    ]);
  };

  const handleRemove = (index: number) => {
    onTargetsChange(targets.filter((_, i) => i !== index));
  };

  const handleFieldChange = (index: number, fieldInvariantId: string) => {
    const updated = [...targets];
    updated[index] = {
      ...updated[index],
      formFieldInvariantId: fieldInvariantId,
    };
    onTargetsChange(updated);
  };

  const handleAttributesChange = (index: number, values: string[]) => {
    const updated = [...targets];
    updated[index] = {
      ...updated[index],
      attributes: values.map(v => Number(v) as NotificationAttributeEnum),
    };
    onTargetsChange(updated);
  };

  return (
    <Box mb="md" mt="sm">
      <SectionTitle text="Form Field Personnel Targets" />
      <Text size="sm" c="dimmed" mb="sm">
        Assign users based on personnel field values from the triggering form
        submission.
      </Text>

      {targets.map((target, index) => (
        <Group key={index} mb="sm" align="flex-start">
          <Select
            label="Personnel Field"
            data={personnelFieldOptions}
            value={target.formFieldInvariantId}
            onChange={val => val && handleFieldChange(index, val)}
            style={{flex: 1}}
          />
          <MultiSelect
            label="Assign"
            data={PersonnelAttributeOptions}
            value={target.attributes?.map(a => String(a)) ?? []}
            onChange={vals => handleAttributesChange(index, vals)}
            style={{flex: 1}}
          />
          <ActionIcon
            color="red"
            variant="subtle"
            mt={28}
            onClick={() => handleRemove(index)}>
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      ))}

      <Button
        size="xs"
        leftSection={<IconPlus size={14} />}
        onClick={handleAdd}>
        Add Field Target
      </Button>
    </Box>
  );
};
